import { Box } from '@kluein/klue-ui';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useAuth } from 'contexts/auth';
import { useTransitionNavigation } from 'hooks/use-transition-navigation';
import { DashboardView } from 'pages/dashboard';

import ToggleFadedBoardsVisibility from './ToggleFadedBoardsVisibility';

import './AskKluePage.css';
import { AskKlueContent } from '../ask-klue-content';
import { AskKlueShowBoards } from '../ask-klue-show-boards';

type AskKluePageProps = {
  isEmbedded?: boolean;
};

function AskKluePage({ isEmbedded = false }: AskKluePageProps) {
  const { navigateWithTransition } = useTransitionNavigation();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, isAskKlueDashboardPageEnabled, company } = useAuth();
  const [didSubmitToAskKlue, setDidSubmitToAskKlue] = useState(false);
  const [showFadedBoards, setShowFadedBoards] = useState(
    user?.userData?.askKlueDashboardVisibleToggle ?? true,
  );

  // Sync state with user data changes
  useEffect(() => {
    setShowFadedBoards(user?.userData?.askKlueDashboardVisibleToggle ?? true);
  }, [user?.userData?.askKlueDashboardVisibleToggle]);

  useEffect(() => {
    if (didSubmitToAskKlue) {
      setShowFadedBoards(false);
    }
  }, [didSubmitToAskKlue]);

  // Redirect if user doesn't have access to Ask Klue dashboard page
  if (company && !isAskKlueDashboardPageEnabled) {
    return null;
  }

  const handleShowBoardsClick = () => {
    const currentParams = new URLSearchParams(location.search);

    const preserved = new URLSearchParams();

    ['group', 'sort'].forEach((param) => {
      const value = currentParams.get(param);
      if (value) {
        preserved.set(param, value);
      }
    });

    const search = preserved.toString();
    if (search) {
      navigateWithTransition(`/dashboard?${search}`, 'askklue-to-dashboard');
      return;
    }
    navigateWithTransition('/dashboard', 'askklue-to-dashboard');
  };

  const handleToggleBoardsVisibility = () => {
    dispatch.auth.updateUserAskKlueDashboardToggle(!showFadedBoards);
    setShowFadedBoards(!showFadedBoards);
  };

  const handleDidSubmitAskKlue = () => {
    setDidSubmitToAskKlue(true);
  };

  return (
    <Box fill id="ask-klue-page">
      <Box id="ask-klue-page-ask-klue-content">
        <AskKlueContent
          isPage={true}
          onDidSubmitAskKlue={handleDidSubmitAskKlue}
        />
        {!isEmbedded && (
          <>
            <ToggleFadedBoardsVisibility
              boardsVisible={showFadedBoards}
              onToggleBoardsVisibility={handleToggleBoardsVisibility}
            />
            {showFadedBoards && (
              <AskKlueShowBoards
                onClick={handleShowBoardsClick}
                isPage={true}
              />
            )}
          </>
        )}
      </Box>
      {!isEmbedded && showFadedBoards && (
        <Box id="ask-klue-page-dashboard-view">
          <DashboardView />
        </Box>
      )}
    </Box>
  );
}

export default AskKluePage;
